import React, { useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { buildSurveyTeamSlotIso } from '../../api/strapiCrm';
import {
  SCHEDULE_WEEKDAY_HEADER_LABELS,
  isoDatesInclusive,
  scheduleDayHeaderLabel,
  scheduleDayTitle,
  shiftIsoDateByDays,
  weekDatesForwardFrom,
} from '../../sampling/surveyScheduleCalendar';
import type { UnscheduledSampleGroupOption } from '../../pages/SamplingSchedulesPage/samplingSchedulePlans';
import { ScheduleSampleGroupAccordionPicker } from './ScheduleSampleLineAccordionPicker';
import type { TeamMemberAvailability } from './quotationScheduleCalendarUtils';
import {
  WORK_ORDER_STATUSES,
  type WorkOrderStatus,
} from '../../pages/SamplingWorkOrdersPage/workOrderShared';
import {
  defaultRomanMonthForDate,
  romanMonthMenuItems,
  type RomanMonth,
} from '../../pages/SamplingWorkOrdersPage/workOrderRomanMonth';
import { WorkOrderStatusMenuItemLabel, WorkOrderStatusTooltip } from '../../pages/SamplingWorkOrdersPage/WorkOrderStatusHover';

export type SlotAssignConfirmPayload = {
  userId: string;
  userName: string;
  startDate: string;
  finishDate: string;
  /** Tanggal eksplisit (toggle); jika ada, dipakai menggantikan rentang start–finish. */
  dates?: string[];
  time: string;
  isLead: boolean;
  quotationGroupDocumentId?: string;
  quotationRecurringEventDocumentId?: string;
  planDocumentIds?: string[];
  /** @deprecated Legacy single-plan payload */
  planDocumentId?: string;
  sampleLineDocumentId?: string;
};

export type SlotAssignScheduleNotes = {
  notes: string;
  transportasiNote: string;
  /** Nama tim per tanggal (`YYYY-MM-DD` → nama). */
  teamNamesByDate?: Record<string, string>;
};

export type SlotAssignWorkOrderPayload = {
  work_order_date: string;
  progress_status: WorkOrderStatus;
  roman_quarter: RomanMonth;
  calendar_year: number;
};

type SavedMemberEntry = SlotAssignConfirmPayload & { id: string };
export type DateOnlySelection = {
  startDate: string;
  finishDate: string;
  /** Tanggal eksplisit dari mode toggle (admin sampling). */
  dates?: string[];
  planDocumentIds?: string[];
  quotationGroupDocumentId?: string;
  quotationRecurringEventDocumentId?: string;
};
type TeamSuggestion = {
  id: string;
  label: string;
  members: Array<{ userId: string; userName: string; isLead: boolean; timeLabel?: string }>;
};

/** Cara pilih tanggal di strip 7 hari: rentang (admin WO) atau toggle per hari (admin sampling). */
export type SlotAssignDatePickMode = 'range' | 'toggle';

type Props = {
  open: boolean;
  anchorDate: string;
  weekAnchorDate?: string;
  onWeekAnchorDateChange?: (date: string) => void;
  quotationLabel: string;
  availability: TeamMemberAvailability[];
  weekAvailabilityByUserId: Map<string, Map<string, TeamMemberAvailability>>;
  mode?: 'add' | 'edit';
  assignmentMode?: 'team' | 'date_only';
  /**
   * `range` = klik mulai→selesai (admin WO).
   * `toggle` = klik on/off per tanggal (admin sampling).
   * Default: date_only → range, team → toggle.
   */
  datePickMode?: SlotAssignDatePickMode;
  initialEntries?: SlotAssignConfirmPayload[];
  initialDateOnlyRange?: DateOnlySelection | null;
  initialGroupDocumentId?: string | null;
  /** Konsultan: di mode edit, group schedule ini tampil terpilih dan tidak bisa diganti. */
  pinSampleGroupInEdit?: boolean;
  /** Kontrak: group sudah ditentukan oleh jadwal yang dibuka, tidak dipilih lagi. */
  pinSampleGroup?: boolean;
  locked?: boolean;
  requiresSampleGroupSelection?: boolean;
  /** @deprecated Use requiresSampleGroupSelection */
  requiresSampleLineSelection?: boolean;
  sampleGroupOptions?: UnscheduledSampleGroupOption[];
  /** @deprecated Use sampleGroupOptions */
  sampleLineOptions?: UnscheduledSampleGroupOption[];
  sampleGroupsLoading?: boolean;
  teamSuggestions?: TeamSuggestion[];
  scheduleNotes?: string | null;
  scheduleTransportasiNote?: string | null;
  /** Nama tim awal per tanggal saat edit (`YYYY-MM-DD` → nama). */
  initialTeamNamesByDate?: Record<string, string> | null;
  /** @deprecated Gunakan initialTeamNamesByDate */
  initialTeamName?: string | null;
  /** Hanya admin sampling yang boleh mengisi kendaraan. Default true. */
  canEditTransportasiNote?: boolean;
  /** Strict: only admin_sampling may add freelance placeholder from this modal. */
  canAddFreelanceUser?: boolean;
  onCreateFreelanceUser?: (input: {
    name: string;
    phone_number: string;
  }) => Promise<{ userId: string; userName: string }>;
  onClose: () => void;
  onConfirm: (
    payloads: SlotAssignConfirmPayload[],
    scheduleNotes: SlotAssignScheduleNotes,
    dateOnlySelection?: DateOnlySelection,
    workOrder?: SlotAssignWorkOrderPayload,
  ) => void;
  /** Admin WO: tampilkan tab header work order dan simpan sekalian promote WO. */
  canCreateWorkOrder?: boolean;
};

function normalizeCalendarDates(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out = new Set<string>();
  for (const item of value) {
    const date = String(item ?? '').trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) out.add(date);
  }
  return [...out].sort();
}

function datesToRangeBounds(dates: string[]): { startDate: string; finishDate: string } | null {
  const sorted = normalizeCalendarDates(dates);
  if (!sorted.length) return null;
  return { startDate: sorted[0]!, finishDate: sorted[sorted.length - 1]! };
}

function entrySelectedDates(entry: Pick<SlotAssignConfirmPayload, 'dates' | 'startDate' | 'finishDate'>): string[] {
  const fromDates = normalizeCalendarDates(entry.dates);
  if (fromDates.length > 0) return fromDates;
  if (!entry.startDate) return [];
  return isoDatesInclusive(entry.startDate, entry.finishDate || entry.startDate);
}

type PersonDaySlot = {
  date: string;
  time: string;
  entryId: string;
};

type PersonAssignmentGroup = {
  userId: string;
  userName: string;
  isLead: boolean;
  slots: PersonDaySlot[];
  entryIds: string[];
};

/** Gabungkan saved entries per petugas → daftar tanggal+jam. */
export function groupSavedEntriesByPerson(entries: SavedMemberEntry[]): PersonAssignmentGroup[] {
  const byUser = new Map<string, PersonAssignmentGroup>();
  for (const entry of entries) {
    const dates = entrySelectedDates(entry);
    const time = /^\d{2}:\d{2}$/.test(entry.time) ? entry.time : '08:00';
    let group = byUser.get(entry.userId);
    if (!group) {
      group = {
        userId: entry.userId,
        userName: entry.userName,
        isLead: Boolean(entry.isLead),
        slots: [],
        entryIds: [],
      };
      byUser.set(entry.userId, group);
    } else if (entry.isLead) {
      group.isLead = true;
    }
    if (!group.entryIds.includes(entry.id)) group.entryIds.push(entry.id);
    for (const date of dates) {
      const existingIdx = group.slots.findIndex((slot) => slot.date === date);
      if (existingIdx >= 0) {
        group.slots[existingIdx] = { date, time, entryId: entry.id };
      } else {
        group.slots.push({ date, time, entryId: entry.id });
      }
    }
  }
  return [...byUser.values()]
    .map((group) => ({
      ...group,
      slots: [...group.slots].sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .sort((a, b) => {
      if (a.isLead !== b.isLead) return a.isLead ? -1 : 1;
      return a.userName.localeCompare(b.userName, 'id');
    });
}

type DateOfficerSlot = {
  userId: string;
  userName: string;
  time: string;
  isLead: boolean;
};

type DateAssignmentGroup = {
  date: string;
  officers: DateOfficerSlot[];
};

/** Group petugas terpilih per tanggal (untuk tab). */
export function groupSavedEntriesByDate(entries: SavedMemberEntry[]): DateAssignmentGroup[] {
  const byDate = new Map<string, DateOfficerSlot[]>();
  for (const person of groupSavedEntriesByPerson(entries)) {
    for (const slot of person.slots) {
      const list = byDate.get(slot.date) ?? [];
      list.push({
        userId: person.userId,
        userName: person.userName,
        time: slot.time,
        isLead: person.isLead,
      });
      byDate.set(slot.date, list);
    }
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, officers]) => ({
      date,
      officers: officers.sort((a, b) => {
        if (a.isLead !== b.isLead) return a.isLead ? -1 : 1;
        return a.userName.localeCompare(b.userName, 'id');
      }),
    }));
}

/** Seed nama tim per tanggal dari map, atau legacy satu nama untuk semua tanggal. */
export function seedTeamNamesByDate(opts: {
  initialTeamNamesByDate?: Record<string, string> | null;
  initialTeamName?: string | null;
  dates: string[];
}): Record<string, string> {
  const dates = normalizeCalendarDates(opts.dates);
  const fromMap = opts.initialTeamNamesByDate ?? {};
  const hasMap = Object.keys(fromMap).some((key) => String(fromMap[key] ?? '').trim());
  if (hasMap) {
    const out: Record<string, string> = {};
    for (const date of dates) {
      const name = String(fromMap[date] ?? '').trim();
      if (name) out[date] = name;
    }
    // Pertahankan tanggal di map yang mungkin belum ada di strip (masih punya petugas).
    for (const [date, raw] of Object.entries(fromMap)) {
      const name = String(raw ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(date) && name && out[date] == null) out[date] = name;
    }
    return out;
  }
  const legacy = String(opts.initialTeamName ?? '').trim();
  if (!legacy || dates.length === 0) return {};
  return Object.fromEntries(dates.map((date) => [date, legacy]));
}

/** Tanggal yang muncul sebagai tab: tanggal assign + tanggal penjadwalan terpilih. */
export function resolvePetugasTabDates(opts: {
  entries: SavedMemberEntry[];
  selectedDates?: string[];
  rangeStart?: string | null;
  rangeFinish?: string | null;
  datePickMode?: SlotAssignDatePickMode;
}): string[] {
  const dates = new Set<string>();
  for (const group of groupSavedEntriesByDate(opts.entries)) {
    dates.add(group.date);
  }
  if ((opts.datePickMode ?? 'toggle') === 'toggle') {
    for (const date of normalizeCalendarDates(opts.selectedDates)) dates.add(date);
  } else {
    const start = String(opts.rangeStart ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(start)) {
      const finish = String(opts.rangeFinish ?? start).trim() || start;
      for (const date of isoDatesInclusive(start, finish < start ? start : finish)) {
        dates.add(date);
      }
    }
  }
  return [...dates].sort();
}

/** Rebuild saved entries dari slot harian per petugas (satu entry per tanggal). */
function rebuildEntriesFromPersonDaySlots(
  groups: Array<{
    userId: string;
    userName: string;
    isLead: boolean;
    slots: Array<{ date: string; time: string }>;
    meta?: Pick<
      SavedMemberEntry,
      | 'quotationGroupDocumentId'
      | 'quotationRecurringEventDocumentId'
      | 'planDocumentIds'
      | 'planDocumentId'
    >;
  }>,
  entryIdFactory: () => string,
): SavedMemberEntry[] {
  const next: SavedMemberEntry[] = [];
  for (const group of groups) {
    const sortedSlots = [...group.slots]
      .map((slot) => ({
        date: String(slot.date).trim().slice(0, 10),
        time: /^\d{2}:\d{2}$/.test(slot.time) ? slot.time : '08:00',
      }))
      .filter((slot) => /^\d{4}-\d{2}-\d{2}$/.test(slot.date))
      .sort((a, b) => a.date.localeCompare(b.date));
    sortedSlots.forEach((slot, index) => {
      next.push({
        id: entryIdFactory(),
        userId: group.userId,
        userName: group.userName,
        startDate: slot.date,
        finishDate: slot.date,
        dates: [slot.date],
        time: slot.time,
        isLead: group.isLead && index === 0,
        quotationGroupDocumentId: group.meta?.quotationGroupDocumentId,
        quotationRecurringEventDocumentId: group.meta?.quotationRecurringEventDocumentId,
        planDocumentIds: group.meta?.planDocumentIds,
        planDocumentId: group.meta?.planDocumentId,
      });
    });
  }
  return next;
}

/** Bangun payload simpan: irisan tanggal, atau remap jika tanggal strip diganti total. */
export function buildConfirmPayloadsForScheduleDates(
  entries: SavedMemberEntry[],
  scheduleDates: string[],
  meta?: Pick<
    SavedMemberEntry,
    | 'quotationGroupDocumentId'
    | 'quotationRecurringEventDocumentId'
    | 'planDocumentIds'
    | 'planDocumentId'
  >,
): SlotAssignConfirmPayload[] {
  const dates = normalizeCalendarDates(scheduleDates);
  const people = groupSavedEntriesByPerson(entries);
  const out: SlotAssignConfirmPayload[] = [];

  for (const person of people) {
    const metaFromEntry = entries.find((entry) => entry.userId === person.userId);
    const rowMeta = {
      quotationGroupDocumentId:
        meta?.quotationGroupDocumentId ?? metaFromEntry?.quotationGroupDocumentId,
      quotationRecurringEventDocumentId:
        meta?.quotationRecurringEventDocumentId ?? metaFromEntry?.quotationRecurringEventDocumentId,
      planDocumentIds: meta?.planDocumentIds ?? metaFromEntry?.planDocumentIds,
      planDocumentId: meta?.planDocumentId ?? metaFromEntry?.planDocumentId,
    };

    let daySlots = person.slots
      .filter((slot) => dates.length === 0 || dates.includes(slot.date))
      .map((slot) => ({
        date: slot.date,
        time: /^\d{2}:\d{2}$/.test(slot.time) ? slot.time : '08:00',
      }));

    // Strip tanggal diganti total (tidak ada irisan) → pindahkan petugas ke tanggal baru.
    if (daySlots.length === 0 && dates.length > 0 && person.slots.length > 0) {
      daySlots = dates.map((date, index) => {
        const source = person.slots[Math.min(index, person.slots.length - 1)]!;
        const time = /^\d{2}:\d{2}$/.test(source.time) ? source.time : '08:00';
        return { date, time };
      });
    }

    daySlots.forEach((slot, index) => {
      out.push({
        userId: person.userId,
        userName: person.userName,
        startDate: slot.date,
        finishDate: slot.date,
        dates: [slot.date],
        time: slot.time,
        isLead: person.isLead && index === 0,
        quotationGroupDocumentId: rowMeta.quotationGroupDocumentId,
        quotationRecurringEventDocumentId: rowMeta.quotationRecurringEventDocumentId,
        planDocumentIds: rowMeta.planDocumentIds,
        planDocumentId: rowMeta.planDocumentId,
      });
    });
  }

  return out;
}

function availabilityLabel(row: TeamMemberAvailability): string {
  if (!row.available) return `Tidak tersedia — ${row.busyQuotationNumber ?? 'quotation lain'}`;
  if (row.busyTimeLabels.length > 0) {
    return `Terjadwal jam ${row.busyTimeLabels.join(', ')} di ${row.busyQuotationNumber ?? 'quotation lain'}`;
  }
  if (row.assignedOnDate) return 'Sudah di quotation ini';
  return 'Tersedia';
}

/** Rentang yang dipakai saat "Gunakan tim": ikuti pilihan di kalender / rentang WO, bukan 1 hari. */
export function resolveAppliedTeamDateRange(opts: {
  rangeStart: string | null;
  rangeFinish: string | null;
  initialDateOnlyRange?: DateOnlySelection | null;
  fallbackDate: string;
}): { startDate: string; finishDate: string } {
  const selectedStart = opts.rangeStart?.trim() || '';
  if (selectedStart) {
    const selectedFinish = opts.rangeFinish?.trim() || selectedStart;
    return {
      startDate: selectedStart,
      finishDate: selectedFinish < selectedStart ? selectedStart : selectedFinish,
    };
  }

  const initialStart = opts.initialDateOnlyRange?.startDate?.trim() || '';
  if (initialStart) {
    const initialFinish = opts.initialDateOnlyRange?.finishDate?.trim() || initialStart;
    return {
      startDate: initialStart,
      finishDate: initialFinish < initialStart ? initialStart : initialFinish,
    };
  }

  return { startDate: opts.fallbackDate, finishDate: opts.fallbackDate };
}

const ROMAN_MONTH_SET = new Set<string>([
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
]);

export function validateSlotAssignWorkOrder(payload: {
  work_order_date: string;
  roman_quarter: string;
  calendar_year: number;
}): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.work_order_date.trim())) {
    return 'Tanggal work order wajib diisi.';
  }
  if (!ROMAN_MONTH_SET.has(payload.roman_quarter.trim())) {
    return 'Bulan Romawi work order wajib dipilih.';
  }
  if (
    !Number.isFinite(payload.calendar_year) ||
    payload.calendar_year < 1900 ||
    payload.calendar_year > 2100
  ) {
    return 'Tahun kalender work order tidak valid.';
  }
  return null;
}

function validateMemberDraft(
  selectedUserId: string | null,
  rangeStart: string | null,
  rangeFinish: string | null,
  timeInput: string,
  weekDates: string[],
  weekAvailabilityByUserId: Map<string, Map<string, TeamMemberAvailability>>,
  savedEntries: SavedMemberEntry[],
  availability: TeamMemberAvailability[],
): string | null {
  if (!selectedUserId) return 'Pilih petugas sampling terlebih dahulu.';
  if (savedEntries.some((e) => e.userId === selectedUserId)) {
    return 'Petugas ini sudah ada di daftar. Hapus dulu jika ingin mengubah.';
  }
  if (!rangeStart) return 'Pilih tanggal mulai survey.';
  const finish = rangeFinish ?? rangeStart;
  try {
    buildSurveyTeamSlotIso(rangeStart, timeInput);
    buildSurveyTeamSlotIso(finish, timeInput);
  } catch (e) {
    return e instanceof Error ? e.message : 'Jam tidak valid.';
  }

  const userWeek = weekAvailabilityByUserId.get(selectedUserId);
  const targetTime = timeInput.trim();
  if (!userWeek) {
    const row = availability.find((a) => a.userId === selectedUserId);
    if (!row) return null;
    if (!row.available) {
      return `Petugas tidak tersedia pada ${scheduleDayTitle(rangeStart)}.`;
    }
    if (targetTime && row.busyTimeLabels.includes(targetTime)) {
      return `Petugas sudah terjadwal pada jam ${targetTime} di ${scheduleDayTitle(rangeStart)}.`;
    }
    return null;
  }

  for (const date of isoDatesInclusive(rangeStart, finish)) {
    if (!weekDates.includes(date)) {
      return 'Rentang tanggal harus dalam 7 hari ke depan.';
    }
    const day = userWeek.get(date);
    if (!day?.available) {
      return `Petugas tidak tersedia pada ${scheduleDayTitle(date)}.`;
    }
    if (targetTime && day.busyTimeLabels.includes(targetTime)) {
      return `Petugas sudah terjadwal pada jam ${targetTime} di ${scheduleDayTitle(date)}.`;
    }
  }
  return null;
}

function upsertPendingDraftEntry(
  entries: SavedMemberEntry[],
  draft: {
    selectedUserId: string | null;
    selectedUserName?: string;
    rangeStart: string | null;
    rangeFinish: string | null;
    timeInput: string;
    isLead: boolean;
    quotationGroupDocumentId?: string;
    planDocumentIds?: string[];
  },
  entryIdFactory: () => string,
): SavedMemberEntry[] {
  if (!draft.selectedUserId || !draft.rangeStart) return entries;
  const finish = draft.rangeFinish ?? draft.rangeStart;
  const existingIndex = entries.findIndex((entry) => entry.userId === draft.selectedUserId);
  const existing = existingIndex >= 0 ? entries[existingIndex] : null;
  const nextLead =
    draft.isLead ||
    existing?.isLead ||
    !entries.some((entry, index) => index !== existingIndex && entry.isLead);

  const baseEntry: SavedMemberEntry = {
    id: existing?.id ?? entryIdFactory(),
    userId: draft.selectedUserId,
    userName: draft.selectedUserName ?? existing?.userName ?? draft.selectedUserId,
    startDate: draft.rangeStart,
    finishDate: finish,
    time: draft.timeInput,
    isLead: nextLead,
    quotationGroupDocumentId:
      draft.quotationGroupDocumentId ?? existing?.quotationGroupDocumentId,
    planDocumentIds: draft.planDocumentIds ?? existing?.planDocumentIds,
    planDocumentId: (draft.planDocumentIds ?? existing?.planDocumentIds)?.[0],
  };

  const nextEntries =
    existingIndex >= 0
      ? entries.map((entry, index) => (index === existingIndex ? baseEntry : entry))
      : [...entries, baseEntry];

  if (!nextLead) return nextEntries;
  return nextEntries.map((entry) => ({
    ...entry,
    isLead: entry.userId === baseEntry.userId,
  }));
}

export function QuotationScheduleSlotAssignDialog({
  open,
  anchorDate,
  weekAnchorDate,
  onWeekAnchorDateChange,
  quotationLabel,
  availability,
  weekAvailabilityByUserId,
  mode = 'add',
  assignmentMode = 'team',
  datePickMode: datePickModeProp,
  initialEntries = [],
  initialDateOnlyRange = null,
  initialGroupDocumentId = null,
  pinSampleGroupInEdit = false,
  pinSampleGroup = false,
  scheduleNotes = '',
  scheduleTransportasiNote = '',
  canEditTransportasiNote = true,
  locked = false,
  requiresSampleGroupSelection,
  requiresSampleLineSelection = false,
  sampleGroupOptions,
  sampleLineOptions = [],
  sampleGroupsLoading = false,
  teamSuggestions = [],
  canAddFreelanceUser = false,
  onCreateFreelanceUser,
  initialTeamNamesByDate = null,
  initialTeamName = null,
  onClose,
  onConfirm,
  canCreateWorkOrder = false,
}: Props) {
  const requiresGroupSelection = requiresSampleGroupSelection ?? requiresSampleLineSelection;
  const groupOptions = sampleGroupOptions ?? sampleLineOptions;
  const selectableGroupOptions = useMemo(
    () => groupOptions.filter((row) => !row.disabled),
    [groupOptions],
  );
  const datePickMode: SlotAssignDatePickMode =
    datePickModeProp ?? (assignmentMode === 'date_only' ? 'range' : 'toggle');
  const isToggleDatePick = datePickMode === 'toggle';
  const effectiveWeekAnchorDate = weekAnchorDate?.trim() || anchorDate;
  const weekDates = useMemo(
    () => weekDatesForwardFrom(effectiveWeekAnchorDate, 7),
    [effectiveWeekAnchorDate],
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeFinish, setRangeFinish] = useState<string | null>(null);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [timeInput, setTimeInput] = useState('08:00');
  const [isLead, setIsLead] = useState(false);
  const [savedEntries, setSavedEntries] = useState<SavedMemberEntry[]>([]);
  const [notesInput, setNotesInput] = useState('');
  const [transportasiNoteInput, setTransportasiNoteInput] = useState('');
  const [teamNamesByDate, setTeamNamesByDate] = useState<Record<string, string>>({});
  const [addingOfficer, setAddingOfficer] = useState(false);
  const [pendingOfficerId, setPendingOfficerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [entryCounter, setEntryCounter] = useState(0);
  const [addFreelanceOpen, setAddFreelanceOpen] = useState(false);
  const [freelanceName, setFreelanceName] = useState('');
  const [freelancePhone, setFreelancePhone] = useState('');
  const [freelanceError, setFreelanceError] = useState<string | null>(null);
  const [freelanceSaving, setFreelanceSaving] = useState(false);
  const [assignTab, setAssignTab] = useState<'schedule' | 'work_order'>('schedule');
  const [petugasDateTab, setPetugasDateTab] = useState<string | null>(null);
  const [workOrderDate, setWorkOrderDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [workOrderStatus, setWorkOrderStatus] = useState<WorkOrderStatus>('draft');
  const [romanQuarter, setRomanQuarter] = useState<RomanMonth>(() => defaultRomanMonthForDate());
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());
  const romanOptions = useMemo(() => romanMonthMenuItems(), []);

  const dateGroups = useMemo(() => groupSavedEntriesByDate(savedEntries), [savedEntries]);
  const officersByDate = useMemo(() => {
    const map = new Map<string, DateOfficerSlot[]>();
    for (const group of dateGroups) map.set(group.date, group.officers);
    return map;
  }, [dateGroups]);
  const petugasTabDates = useMemo(
    () =>
      resolvePetugasTabDates({
        entries: savedEntries,
        selectedDates,
        rangeStart,
        rangeFinish,
        datePickMode,
      }),
    [savedEntries, selectedDates, rangeStart, rangeFinish, datePickMode],
  );
  const activePetugasDate =
    petugasDateTab && petugasTabDates.includes(petugasDateTab)
      ? petugasDateTab
      : (petugasTabDates[0] ?? null);
  const activeDateOfficers = activePetugasDate
    ? (officersByDate.get(activePetugasDate) ?? [])
    : [];

  const initialEntriesKey = useMemo(() => JSON.stringify(initialEntries), [initialEntries]);
  const initialNotesKey = scheduleNotes?.trim() ?? '';
  const initialTransportasiNoteKey = scheduleTransportasiNote?.trim() ?? '';
  const initialTeamNamesByDateKey = useMemo(() => {
    if (initialTeamNamesByDate && Object.keys(initialTeamNamesByDate).length > 0) {
      return JSON.stringify(initialTeamNamesByDate);
    }
    const legacy = initialTeamName?.trim() ?? '';
    return legacy ? JSON.stringify({ __legacy__: legacy }) : '';
  }, [initialTeamNamesByDate, initialTeamName]);
  const initialRangeKey = initialDateOnlyRange
    ? `${initialDateOnlyRange.startDate}|${initialDateOnlyRange.finishDate ?? initialDateOnlyRange.startDate}|${(initialDateOnlyRange.dates ?? []).join(',')}`
    : '';
  const isEditMode = mode === 'edit';
  const isDateOnlyMode = assignmentMode === 'date_only';
  const showWorkOrderTab = canCreateWorkOrder && isDateOnlyMode && !locked;
  /** Petugas yang sudah ada di tab tanggal aktif (boleh dipilih lagi di tanggal lain). */
  const activeDateOfficerIds = useMemo(
    () => new Set(activeDateOfficers.map((officer) => officer.userId)),
    [activeDateOfficers],
  );
  const addableOfficers = useMemo(
    () => availability.filter((row) => !activeDateOfficerIds.has(row.userId)),
    [availability, activeDateOfficerIds],
  );

  useEffect(() => {
    if (!open) return;
    setPetugasDateTab(null);
  }, [open, initialEntriesKey, initialRangeKey]);

  useEffect(() => {
    if (!petugasTabDates.length) {
      if (petugasDateTab != null) setPetugasDateTab(null);
      return;
    }
    if (!petugasDateTab || !petugasTabDates.includes(petugasDateTab)) {
      setPetugasDateTab(petugasTabDates[0]!);
      setAddingOfficer(false);
      setPendingOfficerId('');
    }
  }, [petugasTabDates, petugasDateTab]);

  function rewriteSavedEntriesFromPersonSlots(
    updater: (groups: PersonAssignmentGroup[]) => PersonAssignmentGroup[],
  ) {
    setSavedEntries((prev) => {
      const current = groupSavedEntriesByPerson(prev);
      const metaByUser = new Map(
        prev.map((entry) => [
          entry.userId,
          {
            quotationGroupDocumentId: entry.quotationGroupDocumentId,
            planDocumentIds: entry.planDocumentIds,
            planDocumentId: entry.planDocumentId,
          },
        ]),
      );
      const nextGroups = updater(current);
      let seq = 0;
      return rebuildEntriesFromPersonDaySlots(
        nextGroups.map((group) => ({
          userId: group.userId,
          userName: group.userName,
          isLead: group.isLead,
          slots: group.slots,
          meta: metaByUser.get(group.userId),
        })),
        () => {
          seq += 1;
          return `entry-${Date.now()}-${seq}`;
        },
      );
    });
  }

  function handleUpdatePersonDayTime(userId: string, date: string, time: string) {
    rewriteSavedEntriesFromPersonSlots((groups) =>
      groups.map((group) => {
        if (group.userId !== userId) return group;
        return {
          ...group,
          slots: group.slots.map((slot) =>
            slot.date === date ? { ...slot, time: time || slot.time } : slot,
          ),
        };
      }),
    );
  }

  function handleRemovePersonDay(userId: string, date: string) {
    rewriteSavedEntriesFromPersonSlots((groups) => {
      const next = groups
        .map((group) => {
          if (group.userId !== userId) return group;
          return {
            ...group,
            slots: group.slots.filter((slot) => slot.date !== date),
          };
        })
        .filter((group) => group.slots.length > 0);
      if (next.length === 0) {
        queueMicrotask(() => setSelectedGroupId(null));
      }
      return next;
    });
  }

  function handleTogglePersonLead(userId: string) {
    rewriteSavedEntriesFromPersonSlots((groups) =>
      groups.map((group) => ({
        ...group,
        isLead: group.userId === userId,
      })),
    );
  }

  function handleAddOfficerFromDropdown(userId: string) {
    if (locked) return;
    const user = availability.find((row) => row.userId === userId);
    if (!user) {
      setError('Petugas tidak ditemukan pada daftar petugas sampling.');
      return;
    }
    const date = activePetugasDate;
    if (!date) {
      setError('Pilih tanggal penjadwalan terlebih dahulu sebelum menambah petugas.');
      return;
    }
    if ((officersByDate.get(date) ?? []).some((officer) => officer.userId === userId)) {
      setError('Petugas sudah ada di tanggal ini.');
      return;
    }
    setError(null);
    const time = timeInput || '08:00';
    const existingPerson = groupSavedEntriesByPerson(savedEntries).find(
      (group) => group.userId === userId,
    );
    if (existingPerson) {
      // Petugas sudah di tanggal lain → tambah slot hanya untuk tab aktif.
      const nextEntryId = `entry-${entryCounter}`;
      rewriteSavedEntriesFromPersonSlots((groups) =>
        groups.map((group) => {
          if (group.userId !== userId) return group;
          return {
            ...group,
            slots: [...group.slots, { date, time, entryId: nextEntryId }],
            entryIds: [...group.entryIds, nextEntryId],
          };
        }),
      );
      setEntryCounter((n) => n + 1);
    } else {
      const nextLead = !savedEntries.some((entry) => entry.isLead);
      setSavedEntries((prev) => [
        ...prev,
        {
          id: `entry-${entryCounter}`,
          userId: user.userId,
          userName: user.name,
          startDate: date,
          finishDate: date,
          dates: [date],
          time,
          isLead: nextLead,
          quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
          quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
          planDocumentIds: selectedGroup?.planDocumentIds,
          planDocumentId: selectedGroup?.planDocumentIds?.[0],
        },
      ]);
      setEntryCounter((n) => n + 1);
    }
    setPendingOfficerId('');
    setAddingOfficer(false);
  }

  useEffect(() => {
    if (!open) return;
    setSelectedUserId(null);
    setSelectedGroupId(null);
    setTimeInput('08:00');
    setIsLead(false);
    setError(null);
    setNotesInput(initialNotesKey);
    setTransportasiNoteInput(initialTransportasiNoteKey);
    setAddingOfficer(false);
    setPendingOfficerId('');
    setAddFreelanceOpen(false);
    setFreelanceName('');
    setFreelancePhone('');
    setFreelanceError(null);
    setFreelanceSaving(false);
    setAssignTab('schedule');
    setWorkOrderDate(new Date().toISOString().slice(0, 10));
    setWorkOrderStatus('draft');
    setRomanQuarter(defaultRomanMonthForDate());
    setCalendarYear(new Date().getFullYear());

    const defaultStart = initialDateOnlyRange?.startDate?.trim() || null;
    const defaultFinish = initialDateOnlyRange?.finishDate?.trim() || defaultStart;
    const seedDates = normalizeCalendarDates(initialDateOnlyRange?.dates);
    const seeded = JSON.parse(initialEntriesKey) as SlotAssignConfirmPayload[];
    const seededDates = normalizeCalendarDates(
      seeded.flatMap((entry) => entrySelectedDates(entry)),
    );
    if (datePickMode === 'toggle') {
      const nextDates =
        seedDates.length > 0 || seededDates.length > 0
          ? normalizeCalendarDates([...seedDates, ...seededDates])
          : defaultStart
            ? [defaultStart]
            : [];
      setSelectedDates(nextDates);
      const bounds = datesToRangeBounds(nextDates);
      setRangeStart(bounds?.startDate ?? defaultStart);
      setRangeFinish(bounds?.finishDate ?? defaultFinish);
      setTeamNamesByDate(
        seedTeamNamesByDate({
          initialTeamNamesByDate,
          initialTeamName,
          dates: nextDates,
        }),
      );
    } else {
      setSelectedDates([]);
      setRangeStart(defaultStart);
      setRangeFinish(defaultFinish);
      const rangeDates =
        defaultStart != null
          ? isoDatesInclusive(defaultStart, defaultFinish ?? defaultStart)
          : [];
      setTeamNamesByDate(
        seedTeamNamesByDate({
          initialTeamNamesByDate,
          initialTeamName,
          dates: normalizeCalendarDates([...rangeDates, ...seededDates]),
        }),
      );
    }

    if (!isDateOnlyMode && seeded.length > 0) {
      setSavedEntries(
        seeded.map((entry, index) => {
          const dates = entrySelectedDates(entry);
          const bounds = datesToRangeBounds(dates);
          return {
            ...entry,
            id: `entry-${index}`,
            dates,
            startDate: bounds?.startDate ?? entry.startDate,
            finishDate: bounds?.finishDate ?? entry.finishDate,
          };
        }),
      );
      setEntryCounter(seeded.length);
      return;
    }

    setSavedEntries([]);
    setEntryCounter(0);
  }, [
    open,
    anchorDate,
    initialEntriesKey,
    initialNotesKey,
    initialTransportasiNoteKey,
    initialTeamNamesByDateKey,
    isDateOnlyMode,
    initialRangeKey,
    datePickMode,
  ]);

  useEffect(() => {
    if (!selectedUserId) return;
    setIsLead(false);
    setError(null);
  }, [selectedUserId]);

  useEffect(() => {
    setSelectedUserId(null);
    setIsLead(false);
    setError(null);
  }, [selectedGroupId]);

  const activeGroupId =
    selectedGroupId ??
    savedEntries[0]?.quotationGroupDocumentId?.trim() ??
    initialGroupDocumentId?.trim() ??
    null;
  const selectedGroup = groupOptions.find((row) => {
    const key = row.selectionKey?.trim() || row.quotationGroupDocumentId;
    if (key === activeGroupId) return true;
    return row.quotationGroupDocumentId === activeGroupId;
  });
  const sampleGroupPinned = pinSampleGroup || (isEditMode && pinSampleGroupInEdit);
  const groupSelectionLocked =
    sampleGroupPinned || (Boolean(activeGroupId) && savedEntries.length > 0);
  const sampleGroupReady =
    !requiresGroupSelection || (Boolean(selectedGroup) && !selectedGroup?.disabled);

  const weekForUser = selectedUserId ? weekAvailabilityByUserId.get(selectedUserId) : undefined;
  const selectedUser = availability.find((row) => row.userId === selectedUserId);

  function handleDayClick(date: string) {
    if (locked || (!isEditMode && !sampleGroupReady)) return;

    if (isToggleDatePick) {
      setSelectedDates((prev) => {
        const next = prev.includes(date)
          ? prev.filter((d) => d !== date)
          : [...prev, date].sort();
        const bounds = datesToRangeBounds(next);
        setRangeStart(bounds?.startDate ?? null);
        setRangeFinish(bounds?.finishDate ?? null);
        return next;
      });
      return;
    }

    if (!rangeStart || (rangeStart && rangeFinish)) {
      setRangeStart(date);
      setRangeFinish(null);
      return;
    }
    if (date < rangeStart) {
      setRangeStart(date);
      setRangeFinish(null);
      return;
    }
    setRangeFinish(date);
  }

  function resetDraftForm() {
    setSelectedUserId(null);
    setIsLead(false);
  }

  function handleSaveMember() {
    setError(null);
    if (requiresGroupSelection && !selectedGroup) {
      setError('Pilih group sample terlebih dahulu.');
      return;
    }
    if (selectedGroup?.disabled) {
      setError(selectedGroup.disabledReason ?? 'Group sample ini tidak dapat dijadwalkan.');
      return;
    }
    const validationError = validateMemberDraft(
      selectedUserId,
      rangeStart,
      rangeFinish,
      timeInput,
      weekDates,
      weekAvailabilityByUserId,
      savedEntries,
      availability,
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    const user = availability.find((row) => row.userId === selectedUserId)!;
    const finish = rangeFinish ?? rangeStart!;
    const nextLead = isLead || !savedEntries.some((e) => e.isLead);
    const dates = isToggleDatePick
      ? normalizeCalendarDates(selectedDates)
      : isoDatesInclusive(rangeStart!, finish);

    setSavedEntries((prev) => {
      const normalized = nextLead
        ? prev.map((e) => ({ ...e, isLead: false }))
        : prev;
      return [
        ...normalized,
        {
          id: `entry-${entryCounter}`,
          userId: user.userId,
          userName: user.name,
          startDate: rangeStart!,
          finishDate: finish,
          dates,
          time: timeInput,
          isLead: nextLead,
          quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
          quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
          planDocumentIds: selectedGroup?.planDocumentIds,
          planDocumentId: selectedGroup?.planDocumentIds[0],
        },
      ];
    });
    setEntryCounter((n) => n + 1);
    resetDraftForm();
  }

  function handleApplyTeamSuggestion(team: TeamSuggestion) {
    if (locked) return;
    if (requiresGroupSelection && !selectedGroup) {
      setError('Pilih group sample terlebih dahulu sebelum memakai tim yang sudah ada.');
      return;
    }
    if (team.members.length === 0) return;
    const resolvedMembers = team.members
      .map((member) => {
        const byId = availability.find((row) => row.userId === member.userId);
        if (byId) return { ...member, userId: byId.userId, userName: byId.name };
        const byName = availability.find((row) => row.name === member.userName);
        if (byName) return { ...member, userId: byName.userId, userName: byName.name };
        return null;
      })
      .filter((member): member is NonNullable<typeof member> => member != null);
    if (resolvedMembers.length === 0) {
      setError('Anggota tim tidak ditemukan pada daftar petugas aktif.');
      return;
    }
    setError(null);
    const appliedRange = resolveAppliedTeamDateRange({
      rangeStart,
      rangeFinish,
      initialDateOnlyRange,
      fallbackDate: anchorDate,
    });
    const appliedDates = isToggleDatePick
      ? normalizeCalendarDates(selectedDates.length ? selectedDates : initialDateOnlyRange?.dates)
      : isoDatesInclusive(appliedRange.startDate, appliedRange.finishDate);
    const dates =
      appliedDates.length > 0
        ? appliedDates
        : isoDatesInclusive(appliedRange.startDate, appliedRange.finishDate);
    const bounds = datesToRangeBounds(dates) ?? appliedRange;
    setRangeStart(bounds.startDate);
    setRangeFinish(bounds.finishDate);
    if (isToggleDatePick) setSelectedDates(dates);
    setSavedEntries(
      resolvedMembers.map((member, index) => ({
        id: `entry-${entryCounter + index}`,
        userId: member.userId,
        userName: member.userName,
        startDate: bounds.startDate,
        finishDate: bounds.finishDate,
        dates,
        time: /^\d{2}:\d{2}$/.test(member.timeLabel ?? '') ? String(member.timeLabel) : timeInput,
        isLead: index === 0 ? true : member.isLead,
        quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
        quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
        planDocumentIds: selectedGroup?.planDocumentIds,
        planDocumentId: selectedGroup?.planDocumentIds?.[0],
      })),
    );
    setEntryCounter((n) => n + resolvedMembers.length);
    // Saat memilih tim existing, edit dilakukan di grid petugas terpilih.
    setSelectedUserId(null);
    setIsLead(false);
  }

  async function handleCreateFreelanceUser() {
    if (!onCreateFreelanceUser) return;
    setFreelanceError(null);
    const name = freelanceName.trim();
    if (!name) {
      setFreelanceError('Nama wajib diisi.');
      return;
    }
    setFreelanceSaving(true);
    try {
      const created = await onCreateFreelanceUser({
        name,
        phone_number: freelancePhone.trim(),
      });
      setAddFreelanceOpen(false);
      setFreelanceName('');
      setFreelancePhone('');
      setSelectedUserId(created.userId);
      setError(null);
    } catch (e) {
      setFreelanceError(e instanceof Error ? e.message : 'Gagal menambah freelance user.');
    } finally {
      setFreelanceSaving(false);
    }
  }

  function scheduleNotesPayload(): SlotAssignScheduleNotes {
    const cleaned: Record<string, string> = {};
    for (const [date, raw] of Object.entries(teamNamesByDate)) {
      const name = String(raw ?? '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(date) && name) cleaned[date] = name;
    }
    return {
      notes: notesInput.trim(),
      transportasiNote: transportasiNoteInput.trim(),
      teamNamesByDate: cleaned,
    };
  }

  function handleConfirmAll() {
    setError(null);
    if (isDateOnlyMode) {
      const toggleDates = isToggleDatePick ? normalizeCalendarDates(selectedDates) : [];
      const start = isToggleDatePick ? toggleDates[0] : rangeStart;
      if (!start) {
        setError(
          isToggleDatePick
            ? 'Pilih minimal satu tanggal penjadwalan.'
            : 'Pilih tanggal mulai penjadwalan terlebih dahulu.',
        );
        return;
      }
      if (requiresGroupSelection && !selectedGroup) {
        setError('Pilih group sample terlebih dahulu.');
        return;
      }
      const finish = isToggleDatePick
        ? toggleDates[toggleDates.length - 1]!
        : (rangeFinish ?? rangeStart!);
      let workOrder: SlotAssignWorkOrderPayload | undefined;
      if (showWorkOrderTab) {
        const woError = validateSlotAssignWorkOrder({
          work_order_date: workOrderDate,
          roman_quarter: romanQuarter,
          calendar_year: calendarYear,
        });
        if (woError) {
          setAssignTab('work_order');
          setError(woError);
          return;
        }
        workOrder = {
          work_order_date: workOrderDate.trim(),
          progress_status: workOrderStatus,
          roman_quarter: romanQuarter,
          calendar_year: calendarYear,
        };
      }
      onConfirm(
        [],
        scheduleNotesPayload(),
        {
          startDate: start,
          finishDate: finish,
          ...(isToggleDatePick ? { dates: toggleDates } : {}),
          planDocumentIds: selectedGroup?.planDocumentIds,
          quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
          quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
        },
        workOrder,
      );
      onClose();
      return;
    }

    // Edit slot menyimpan tanggal, kendaraan, dan catatan.
    // Petugas pada tanggal baru disalin dari tim survey paling akhir jadwal ini saat simpan.
    if (isEditMode) {
      const toggleDates = isToggleDatePick ? normalizeCalendarDates(selectedDates) : [];
      if (isToggleDatePick ? toggleDates.length === 0 : !rangeStart) {
        setError(
          isToggleDatePick
            ? 'Pilih minimal satu tanggal penjadwalan.'
            : 'Pilih tanggal penjadwalan terlebih dahulu.',
        );
        return;
      }
      const start = isToggleDatePick ? toggleDates[0]! : rangeStart!;
      const finish = isToggleDatePick
        ? toggleDates[toggleDates.length - 1]!
        : (rangeFinish ?? rangeStart!);
      onConfirm(
        [],
        scheduleNotesPayload(),
        {
          startDate: start,
          finishDate: finish,
          ...(isToggleDatePick ? { dates: toggleDates } : {}),
          planDocumentIds: selectedGroup?.planDocumentIds,
          quotationGroupDocumentId:
            selectedGroup?.quotationGroupDocumentId ?? activeGroupId ?? undefined,
          quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
        },
      );
      onClose();
      return;
    }

    // Tim dibentuk via kalender; modal hanya mengedit roster yang sudah ada / tanggal.
    if (savedEntries.length === 0) {
      const toggleDates = isToggleDatePick ? normalizeCalendarDates(selectedDates) : [];
      if (isToggleDatePick ? toggleDates.length === 0 : !rangeStart) {
        if (isEditMode) {
          onConfirm([], scheduleNotesPayload());
          onClose();
          return;
        }
        setError(
          isToggleDatePick
            ? 'Pilih minimal satu tanggal penjadwalan.'
            : 'Pilih tanggal penjadwalan terlebih dahulu.',
        );
        return;
      }
      if (requiresGroupSelection && !selectedGroup) {
        setError('Pilih group sample terlebih dahulu.');
        return;
      }
      const start = isToggleDatePick ? toggleDates[0]! : rangeStart!;
      const finish = isToggleDatePick
        ? toggleDates[toggleDates.length - 1]!
        : (rangeFinish ?? rangeStart!);
      onConfirm(
        [],
        scheduleNotesPayload(),
        {
          startDate: start,
          finishDate: finish,
          ...(isToggleDatePick ? { dates: toggleDates } : {}),
          planDocumentIds: selectedGroup?.planDocumentIds,
          quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
          quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
        },
      );
      onClose();
      return;
    }
    if (!savedEntries.some((e) => e.isLead)) {
      setError('Minimal satu petugas harus ditandai sebagai lead.');
      return;
    }

    const scheduleDates = isToggleDatePick
      ? normalizeCalendarDates(
          selectedDates.length > 0
            ? selectedDates
            : savedEntries.flatMap((entry) => entrySelectedDates(entry)),
        )
      : rangeStart
        ? isoDatesInclusive(rangeStart, rangeFinish ?? rangeStart)
        : normalizeCalendarDates(savedEntries.flatMap((entry) => entrySelectedDates(entry)));

    const payloads = buildConfirmPayloadsForScheduleDates(
      savedEntries,
      scheduleDates,
      selectedGroup
        ? {
            quotationGroupDocumentId: selectedGroup.quotationGroupDocumentId,
            quotationRecurringEventDocumentId: selectedGroup.quotationRecurringEventDocumentId,
            planDocumentIds: selectedGroup.planDocumentIds,
            planDocumentId: selectedGroup.planDocumentIds[0],
          }
        : undefined,
    );

    if (payloads.length === 0) {
      setError('Minimal satu petugas harus punya tanggal penjadwalan.');
      return;
    }
    if (!payloads.some((row) => row.isLead)) {
      payloads[0] = { ...payloads[0]!, isLead: true };
    }

    onConfirm(
      payloads,
      scheduleNotesPayload(),
      scheduleDates.length > 0
        ? {
            startDate: scheduleDates[0]!,
            finishDate: scheduleDates[scheduleDates.length - 1]!,
            dates: scheduleDates,
            planDocumentIds: selectedGroup?.planDocumentIds,
            quotationGroupDocumentId: selectedGroup?.quotationGroupDocumentId,
            quotationRecurringEventDocumentId: selectedGroup?.quotationRecurringEventDocumentId,
          }
        : undefined,
    );
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      data-testid="schedule-quotation-day-assign-dialog"
      PaperProps={{
        sx: {
          width: 'min(1280px, 96vw)',
        },
      }}
    >
      <DialogTitle sx={{ pb: showWorkOrderTab ? 0 : undefined }}>
        {isEditMode ? 'Edit slot quotation' : 'Tambah slot quotation'}
        {showWorkOrderTab ? (
          <Tabs
            value={assignTab}
            onChange={(_, v: 'schedule' | 'work_order') => setAssignTab(v)}
            sx={{ mt: 1, minHeight: 40 }}
          >
            <Tab
              label="Penjadwalan"
              value="schedule"
              data-testid="schedule-assign-tab-schedule"
            />
            <Tab
              label="Work order"
              value="work_order"
              data-testid="schedule-assign-tab-work-order"
            />
          </Tabs>
        ) : null}
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'auto' }}>
        <Box sx={{ display: assignTab === 'work_order' && showWorkOrderTab ? 'none' : 'block' }}>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {quotationLabel} · {isEditMode ? scheduleDayTitle(anchorDate) : `mulai ${scheduleDayTitle(anchorDate)}`}
          </Typography>

          <Typography variant="body2">
            {isDateOnlyMode ? (
              showWorkOrderTab ? (
                sampleGroupPinned ? (
                  <>
                    Group sample mengikuti jadwal ini. Pilih tanggal di tab <strong>Penjadwalan</strong>,
                    lengkapi header work order di tab <strong>Work order</strong>, lalu klik{' '}
                    <strong>Simpan tanggal & work order</strong>. Tim survey dibentuk lewat kalender
                    (seret kartu ke tim yang sudah ada).
                  </>
                ) : (
                <>
                  Pilih tanggal (dan group sample jika ada) di tab <strong>Penjadwalan</strong>,
                  lengkapi header work order di tab <strong>Work order</strong>, lalu klik{' '}
                  <strong>Simpan tanggal & work order</strong>. Tim survey dibentuk lewat kalender
                  (seret kartu ke tim yang sudah ada).
                </>
                )
              ) : (
                <>
                  Pilih tanggal penjadwalan, lalu klik <strong>Simpan tanggal</strong>. Tim survey
                  dibentuk lewat kalender.
                </>
              )
            ) : isEditMode ? (
              <>
                Pilih tanggal penjadwalan, isi transportasi kendaraan dan catatan penjadwalan, lalu
                klik <strong>Simpan</strong>. Petugas diatur dari ikon pensil pada kartu tim.
              </>
            ) : requiresGroupSelection && !sampleGroupPinned ? (
              <>
                Pilih <strong>satu group sample</strong> dan tanggal, lalu tambah petugas sampling
                atau simpan tanggal dulu.
              </>
            ) : sampleGroupPinned ? (
              <>
                Group sample mengikuti jadwal ini. Pilih tanggal, lalu simpan.
              </>
            ) : (
              <>
                Atur tanggal, tambah petugas sampling, lalu simpan.
              </>
            )}
          </Typography>

          {requiresGroupSelection && sampleGroupsLoading ? (
            <Alert severity="info">Memuat group sample…</Alert>
          ) : null}

          {requiresGroupSelection && !sampleGroupsLoading && selectableGroupOptions.length === 0 ? (
            <Alert severity="info">
              {sampleGroupPinned
                ? 'Group sample jadwal ini belum terhubung.'
                : 'Semua group sample sudah dijadwalkan atau work order telah dibuat.'}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(!isEditMode || sampleGroupPinned) && groupOptions.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {requiresGroupSelection ? 'Group sample' : 'Detail sample line'}
                  </Typography>
                  {groupSelectionLocked ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {sampleGroupPinned
                        ? 'Group pada penjadwalan ini.'
                        : 'Group terkunci untuk jadwal yang sudah punya petugas.'}
                    </Typography>
                  ) : null}
                  <ScheduleSampleGroupAccordionPicker
                    options={groupOptions}
                    selectedGroupId={activeGroupId}
                    locked={locked}
                    selectionLocked={groupSelectionLocked}
                    selectionEnabled={requiresGroupSelection && !sampleGroupPinned}
                    onSelectGroup={setSelectedGroupId}
                  />
                </Box>
              ) : null}

              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    {isToggleDatePick ? 'Tanggal survey (7 hari)' : 'Rentang survey (7 hari)'}
                  </Typography>
                  <Stack direction="row" spacing={0.75}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onWeekAnchorDateChange?.(shiftIsoDateByDays(effectiveWeekAnchorDate, -7))
                      }
                      disabled={!onWeekAnchorDateChange}
                    >
                      Prev week
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        onWeekAnchorDateChange?.(shiftIsoDateByDays(effectiveWeekAnchorDate, 7))
                      }
                      disabled={!onWeekAnchorDateChange}
                    >
                      Next week
                    </Button>
                  </Stack>
                </Stack>
                {requiresGroupSelection && !sampleGroupReady && !isEditMode && !sampleGroupPinned ? (
                  <Alert severity="info">Pilih group sample terlebih dahulu.</Alert>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Tanggal penjadwalan
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                        gap: 0.5,
                      }}
                    >
                      {weekDates.map((date) => {
                        const day = weekForUser?.get(date);
                        const inRange =
                          !isToggleDatePick &&
                          Boolean(rangeStart) &&
                          Boolean(rangeFinish ?? rangeStart) &&
                          date >= rangeStart! &&
                          date <= (rangeFinish ?? rangeStart!);
                        const toggledOn = isToggleDatePick && selectedDates.includes(date);
                        const selected = isToggleDatePick ? toggledOn : inRange;
                        const isStart = !isToggleDatePick && date === rangeStart;
                        const isFinish =
                          !isToggleDatePick && date === (rangeFinish ?? rangeStart) && Boolean(rangeFinish);
                        const palette = selected
                          ? {
                              bgcolor: 'primary.main',
                              borderColor: 'primary.dark',
                              color: 'primary.contrastText',
                            }
                          : {
                              bgcolor: 'grey.300',
                              borderColor: 'grey.500',
                              color: 'text.primary',
                            };

                        return (
                          <Box
                            key={date}
                            component={!locked ? 'button' : 'div'}
                            type={!locked ? 'button' : undefined}
                            title={
                              day
                                ? `${scheduleDayTitle(date)} · ${availabilityLabel(day)}`
                                : scheduleDayTitle(date)
                            }
                            onClick={!locked ? () => handleDayClick(date) : undefined}
                            data-testid={`schedule-assign-week-day-${date}`}
                            aria-pressed={selected}
                            sx={{
                              minHeight: 72,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: palette.borderColor,
                              bgcolor: palette.bgcolor,
                              color: palette.color,
                              p: 0.5,
                              cursor: !locked ? 'pointer' : 'default',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.25,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                              {scheduleDayHeaderLabel(date)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.1 }}>
                              {SCHEDULE_WEEKDAY_HEADER_LABELS[new Date(`${date}T00:00:00`).getDay()]}
                            </Typography>
                            {isStart ? (
                              <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 700 }}>
                                Mulai
                              </Typography>
                            ) : null}
                            {isFinish ? (
                              <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 700 }}>
                                Selesai
                              </Typography>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {isToggleDatePick
                        ? 'Klik tanggal untuk memilih atau membatalkan. Boleh tidak berurutan.'
                        : 'Klik tanggal mulai, lalu tanggal selesai. Satu klik saja = survey 1 hari.'}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {!isDateOnlyMode && !isEditMode ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ px: 1.5, py: 1, bgcolor: 'grey.100' }}
                  >
                    <Typography variant="subtitle2">Petugas terpilih</Typography>
                  </Stack>
                  {petugasTabDates.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 1.5, py: 2 }}
                      data-testid="schedule-assign-saved-list-empty"
                    >
                      Belum ada tanggal. Pilih tanggal penjadwalan dulu, lalu atur nama tim dan
                      petugas pada tab tanggal yang aktif.
                    </Typography>
                  ) : (
                    <Box data-testid="schedule-assign-saved-list">
                      <Tabs
                        value={activePetugasDate ?? false}
                        onChange={(_, value: string) => {
                          setPetugasDateTab(value);
                          setAddingOfficer(false);
                          setPendingOfficerId('');
                          setError(null);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          minHeight: 40,
                          borderBottom: 1,
                          borderColor: 'divider',
                          px: 0.5,
                          '& .MuiTab-root': { minHeight: 40, textTransform: 'none', py: 0.75 },
                        }}
                        data-testid="schedule-assign-petugas-date-tabs"
                      >
                        {petugasTabDates.map((date) => (
                          <Tab
                            key={date}
                            value={date}
                            label={scheduleDayHeaderLabel(date)}
                            data-testid={`schedule-assign-petugas-tab-${date}`}
                          />
                        ))}
                      </Tabs>

                      {activePetugasDate ? (
                        <Box
                          sx={{ px: 1.5, py: 1.25 }}
                          data-testid={`schedule-assign-petugas-panel-${activePetugasDate}`}
                        >
                          {!locked ? (
                            <TextField
                              label="Nama tim"
                              size="small"
                              fullWidth
                              value={teamNamesByDate[activePetugasDate] ?? ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setTeamNamesByDate((prev) => ({
                                  ...prev,
                                  [activePetugasDate]: value,
                                }));
                              }}
                              placeholder="Contoh: Tim A / Tim Pagi"
                              sx={{ mb: 1.25 }}
                              inputProps={{
                                'data-testid': `schedule-assign-team-name-${activePetugasDate}`,
                              }}
                            />
                          ) : (teamNamesByDate[activePetugasDate] ?? '').trim() ? (
                            <Typography variant="body2" sx={{ mb: 1.25 }}>
                              Nama tim: {(teamNamesByDate[activePetugasDate] ?? '').trim()}
                            </Typography>
                          ) : null}

                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {scheduleDayTitle(activePetugasDate)}
                            </Typography>
                            {!locked ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setError(null);
                                  setAddingOfficer((prev) => !prev);
                                  setPendingOfficerId('');
                                }}
                                data-testid="schedule-assign-add-officer"
                              >
                                {addingOfficer ? 'Tutup' : 'Tambah petugas'}
                              </Button>
                            ) : null}
                          </Stack>

                          {addingOfficer && !locked ? (
                            <Stack spacing={1} sx={{ mb: 1.25 }}>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                label="Petugas sampling"
                                value={pendingOfficerId}
                                onChange={(e) => setPendingOfficerId(e.target.value)}
                                disabled={addableOfficers.length === 0}
                                SelectProps={{ displayEmpty: true }}
                                helperText={`Ditambahkan ke ${scheduleDayHeaderLabel(activePetugasDate)}`}
                                inputProps={{
                                  'data-testid': 'schedule-assign-add-officer-select',
                                }}
                              >
                                <MenuItem value="">
                                  <em>
                                    {addableOfficers.length === 0
                                      ? 'Semua petugas sudah dipilih pada tanggal ini'
                                      : 'Pilih petugas…'}
                                  </em>
                                </MenuItem>
                                {addableOfficers.map((row) => (
                                  <MenuItem key={row.userId} value={row.userId}>
                                    {row.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <Button
                                size="small"
                                variant="contained"
                                sx={{ alignSelf: 'flex-start' }}
                                disabled={!pendingOfficerId}
                                onClick={() => handleAddOfficerFromDropdown(pendingOfficerId)}
                                data-testid="schedule-assign-add-officer-confirm"
                              >
                                Tambahkan
                              </Button>
                            </Stack>
                          ) : null}

                          {activeDateOfficers.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Belum ada petugas pada tanggal ini. Klik tambah petugas untuk
                              assign ke tab ini.
                            </Typography>
                          ) : (
                            <Stack spacing={0.75}>
                              <Stack direction="row" spacing={1} sx={{ px: 0.25 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
                                >
                                  Petugas
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ width: 112, flexShrink: 0, fontWeight: 600 }}
                                >
                                  Jam
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ width: 72, flexShrink: 0, fontWeight: 600 }}
                                >
                                  Lead
                                </Typography>
                                {!locked ? (
                                  <Box sx={{ width: 34, flexShrink: 0 }} />
                                ) : null}
                              </Stack>
                              {activeDateOfficers.map((officer) => (
                                <Stack
                                  key={`${activePetugasDate}-${officer.userId}`}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  data-testid={`schedule-assign-saved-row-${officer.userId}`}
                                >
                                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                                    {officer.userName}
                                  </Typography>
                                  {!locked ? (
                                    <TextField
                                      type="time"
                                      size="small"
                                      value={officer.time}
                                      onChange={(e) =>
                                        handleUpdatePersonDayTime(
                                          officer.userId,
                                          activePetugasDate,
                                          e.target.value,
                                        )
                                      }
                                      inputProps={{
                                        step: 300,
                                        'aria-label': `Jam ${officer.userName} ${activePetugasDate}`,
                                        'data-testid': `schedule-assign-saved-time-${officer.userId}-${activePetugasDate}`,
                                      }}
                                      sx={{ width: 112, flexShrink: 0 }}
                                    />
                                  ) : (
                                    <Typography variant="body2" sx={{ width: 112, flexShrink: 0 }}>
                                      {officer.time}
                                    </Typography>
                                  )}
                                  <Box sx={{ width: 72, flexShrink: 0 }}>
                                    {!locked ? (
                                      <Checkbox
                                        size="small"
                                        checked={officer.isLead}
                                        onChange={(_, checked) => {
                                          if (checked) handleTogglePersonLead(officer.userId);
                                        }}
                                        inputProps={{
                                          'aria-label': `Lead ${officer.userName}`,
                                        }}
                                      />
                                    ) : officer.isLead ? (
                                      <Typography
                                        variant="caption"
                                        color="primary.main"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        Lead
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        —
                                      </Typography>
                                    )}
                                  </Box>
                                  {!locked ? (
                                    <IconButton
                                      size="small"
                                      aria-label={`Hapus ${officer.userName} dari ${activePetugasDate}`}
                                      onClick={() =>
                                        handleRemovePersonDay(officer.userId, activePetugasDate)
                                      }
                                      data-testid={`schedule-assign-remove-day-${officer.userId}-${activePetugasDate}`}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  ) : null}
                                </Stack>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Box>
              ) : null}

              <Stack spacing={1.25}>
                {canEditTransportasiNote ? (
                  <TextField
                    label="Transportasi / kendaraan"
                    placeholder="Contoh: Avanza B 1234 CD"
                    value={transportasiNoteInput}
                    onChange={(e) => setTransportasiNoteInput(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={4}
                    size="small"
                    fullWidth
                    disabled={locked}
                    inputProps={{ 'data-testid': 'schedule-assign-transportasi-note' }}
                  />
                ) : null}
                <TextField
                  label="Catatan penjadwalan"
                  placeholder="Catatan penjadwalan..."
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={6}
                  size="small"
                  fullWidth
                  disabled={locked}
                  inputProps={{ 'data-testid': 'schedule-assign-notes' }}
                />
              </Stack>
            </Box>
          </Stack>

          {error && assignTab !== 'work_order' ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
        </Box>

        {showWorkOrderTab ? (
          <Box
            sx={{ display: assignTab === 'work_order' ? 'block' : 'none', pt: 0.5 }}
            data-testid="schedule-assign-work-order-panel"
          >
            <Stack spacing={2} sx={{ maxWidth: 560 }}>
              <Typography variant="body2">
                Header work order tersimpan bersama tanggal penjadwalan. Nomor job terbit setelah
                admin sampling menugaskan petugas lalu menyetujui tim survey.
              </Typography>
              <TextField
                label="Tanggal work order"
                type="date"
                value={workOrderDate}
                onChange={(ev) => setWorkOrderDate(ev.target.value)}
                required
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={locked}
                inputProps={{ 'data-testid': 'schedule-assign-wo-date' }}
              />
              <WorkOrderStatusTooltip status={workOrderStatus}>
                <TextField
                  select
                  label="Status"
                  value={workOrderStatus}
                  onChange={(ev) => setWorkOrderStatus(ev.target.value as WorkOrderStatus)}
                  required
                  fullWidth
                  size="small"
                  disabled={locked}
                  SelectProps={{
                    SelectDisplayProps: {
                      id: 'schedule-assign-wo-status-trigger',
                    },
                  }}
                >
                  {WORK_ORDER_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      <WorkOrderStatusMenuItemLabel status={s} />
                    </MenuItem>
                  ))}
                </TextField>
              </WorkOrderStatusTooltip>
              <TextField
                select
                label="Bulan (angka Romawi)"
                value={romanQuarter}
                onChange={(ev) => setRomanQuarter(ev.target.value as RomanMonth)}
                required
                fullWidth
                size="small"
                disabled={locked}
                helperText="Default mengikuti bulan kalender saat ini (I = Januari … XII = Desember)."
                SelectProps={{
                  SelectDisplayProps: {
                    id: 'schedule-assign-wo-roman-trigger',
                  },
                }}
              >
                {romanOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tahun kalender"
                type="number"
                value={calendarYear}
                onChange={(ev) => {
                  const n = Number.parseInt(ev.target.value, 10);
                  setCalendarYear(Number.isFinite(n) ? n : new Date().getFullYear());
                }}
                required
                fullWidth
                size="small"
                disabled={locked}
                inputProps={{ min: 1900, max: 2100, 'data-testid': 'schedule-assign-wo-year' }}
              />
              <Typography variant="caption" color="text.secondary">
                Tanggal pengujian mengikuti rentang penjadwalan yang dipilih di tab Penjadwalan.
              </Typography>
              {error && assignTab === 'work_order' ? (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        {!locked ? (
          <Button
            variant="contained"
            onClick={handleConfirmAll}
            disabled={false}
            data-testid="schedule-quotation-day-assign-confirm"
          >
            {showWorkOrderTab
              ? 'Simpan tanggal & work order'
              : isDateOnlyMode || savedEntries.length === 0
                ? 'Simpan tanggal'
                : isEditMode
                  ? 'Simpan'
                  : 'Simpan'}
          </Button>
        ) : null}
      </DialogActions>

      <Dialog
        open={addFreelanceOpen}
        onClose={() => (!freelanceSaving ? setAddFreelanceOpen(false) : undefined)}
        maxWidth="xs"
        fullWidth
        data-testid="schedule-assign-add-freelance-dialog"
      >
        <DialogTitle>Tambah freelance sampling</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Placeholder tanpa akun login. Tipe otomatis: petugas sampling.
            </Typography>
            <TextField
              label="Nama"
              size="small"
              required
              value={freelanceName}
              onChange={(e) => setFreelanceName(e.target.value)}
              inputProps={{ 'data-testid': 'schedule-assign-freelance-name' }}
            />
            <TextField
              label="Nomor HP"
              size="small"
              value={freelancePhone}
              onChange={(e) => setFreelancePhone(e.target.value)}
              inputProps={{ 'data-testid': 'schedule-assign-freelance-phone' }}
            />
            {freelanceError ? (
              <Typography variant="body2" color="error">
                {freelanceError}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFreelanceOpen(false)} disabled={freelanceSaving}>
            Batal
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateFreelanceUser()}
            disabled={freelanceSaving}
            data-testid="schedule-assign-freelance-save"
          >
            {freelanceSaving ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
